import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { Mail, Pencil, ShieldOff, UserRoundCog } from 'lucide-react';
import { adminQueries, getErrorMessage, keys } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import { formatDateTime, hasPermission, labelOf, LOCALE_LABELS, ROLE_LABELS } from '@blimo/shared';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, ConfirmDialog, DescriptionList, ErrorState, PageHeader, StatusBadge, toast } from '@blimo/ui';
import { DetailSkeleton } from '../../components/data.jsx';
import { UserFormSheet } from './user-form-sheet.jsx';
import { canImpersonate, canManageUser, isPrivileged } from './user-utils.js';
import { DirectPermissionsCard } from './direct-permissions.jsx';
import { AccessExpiry } from './access-expiry.jsx';

export function UserDetailPage() {
  const api = useApi();
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const { userId } = useParams({ strict: false });
  const [editOpen, setEditOpen] = useState(false);
  const [confirm, setConfirm] = useState(/** @type {null|'two-factor'|'impersonate'} */ (null));
  const { data: user, error, isPending, refetch } = useQuery(adminQueries.user(api, userId));

  const invite = useMutation({
    mutationFn: () => api.admin.users.invite(userId),
    onSuccess: () => toast.success('Einladung versendet'),
    onError: (e) => toast.error(getErrorMessage(e, 'Die Einladung konnte nicht versendet werden.')),
  });

  async function resetTwoFactor() {
    try {
      await api.admin.users.resetTwoFactor(userId);
      await queryClient.invalidateQueries({ queryKey: keys.admin.detail('users', userId) });
      queryClient.invalidateQueries({ queryKey: ['blimo', 'admin', 'users', 'list'] });
      toast.success('Zwei-Faktor-Authentifizierung zurückgesetzt');
    } catch (e) {
      toast.error(getErrorMessage(e, '2FA konnte nicht zurückgesetzt werden.'));
    }
  }

  async function impersonate() {
    try {
      const impersonated = await api.admin.users.impersonate(userId);
      // The session now belongs to the impersonated user: drop admin data, then reload /me.
      queryClient.removeQueries({ queryKey: keys.admin.all });
      queryClient.setQueryData(keys.me(), impersonated);
      await queryClient.invalidateQueries({ queryKey: keys.me() });
      toast.success(`Sie handeln jetzt als ${impersonated?.name ?? user.name}.`);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Die Benutzeransicht konnte nicht gestartet werden.'));
    }
  }

  const breadcrumb = (
    <Link to="/administration/benutzer" className="hover:underline">
      Benutzer & Zugänge
    </Link>
  );

  if (error) {
    return (
      <>
        <PageHeader title="Benutzer" breadcrumb={breadcrumb} />
        <ErrorState error={error} onRetry={() => refetch()} notFoundTitle="Benutzer nicht gefunden" />
      </>
    );
  }
  if (isPending) return <DetailSkeleton />;

  const manage = canManageUser(me, user);
  const mayImpersonate = canImpersonate(me, user);

  return (
    <>
      <PageHeader
        breadcrumb={breadcrumb}
        title={user.name}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <span className="break-all">{user.email}</span>
            <StatusBadge domain="user" status={user.status} />
          </span>
        }
        actions={
          <>
            {manage && (
              <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>
                <Pencil aria-hidden="true" /> Bearbeiten
              </Button>
            )}
            {manage && user.status !== 'locked' && (
              <Button size="sm" variant="outline" onClick={() => invite.mutate()} loading={invite.isPending}>
                <Mail aria-hidden="true" /> Einladung senden
              </Button>
            )}
            {manage && user.two_factor_enabled && (
              <Button size="sm" variant="outline" onClick={() => setConfirm('two-factor')}>
                <ShieldOff aria-hidden="true" /> 2FA zurücksetzen
              </Button>
            )}
            {mayImpersonate && (
              <Button size="sm" variant="outline" onClick={() => setConfirm('impersonate')}>
                <UserRoundCog aria-hidden="true" /> Als Benutzer anmelden
              </Button>
            )}
          </>
        }
      />
      {!manage && hasPermission(me, 'users.manage') && isPrivileged(user) && (
        <p className="mb-3 text-sm text-muted-foreground">
          Privilegierte Konten (Super-Admin, Administrator) können nur mit dem Recht „Rollen verwalten“ bearbeitet werden.
        </p>
      )}
      <div className="grid gap-3 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle as="h2">Konto</CardTitle>
          </CardHeader>
          <CardContent>
            <DescriptionList
              items={[
                { label: 'Name', value: user.name },
                { label: 'E-Mail', value: <span className="break-all">{user.email}</span> },
                { label: 'Status', value: <StatusBadge domain="user" status={user.status} /> },
                {
                  label: 'Rollen',
                  value: user.roles?.length ? (
                    <span className="flex flex-wrap gap-1">
                      {user.roles.map((r) => (
                        <Badge key={r} variant="secondary">
                          {ROLE_LABELS[r] ?? r}
                        </Badge>
                      ))}
                    </span>
                  ) : null,
                },
                { label: 'Sprache', value: labelOf(LOCALE_LABELS, user.locale) },
                {
                  label: 'Verknüpfte Person',
                  value: user.party_id ? (
                    hasPermission(me, 'parties.view') ? (
                      <Link to="/personen/$partyId" params={{ partyId: String(user.party_id) }} className="text-primary hover:underline">
                        {user.party?.display_name ?? `Person #${user.party_id}`}
                      </Link>
                    ) : (
                      (user.party?.display_name ?? `#${user.party_id}`)
                    )
                  ) : null,
                },
              ]}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle as="h2">Sicherheit & Zugang</CardTitle>
          </CardHeader>
          <CardContent>
            <DescriptionList
              items={[
                {
                  label: 'Zwei-Faktor',
                  value: user.two_factor_enabled ? <Badge variant="success">Eingerichtet</Badge> : <Badge variant="neutral">Nicht eingerichtet</Badge>,
                },
                { label: 'Zugang gültig bis', value: <AccessExpiry user={user} /> },
                { label: 'Letzte Anmeldung', value: user.last_login_at ? formatDateTime(user.last_login_at) : 'noch nie' },
                { label: 'Angelegt am', value: user.created_at ? formatDateTime(user.created_at) : null },
              ]}
            />
          </CardContent>
        </Card>
        <DirectPermissionsCard user={user} canEdit={manage} />
      </div>

      {manage && <UserFormSheet user={user} open={editOpen} onOpenChange={setEditOpen} />}
      <ConfirmDialog
        open={confirm === 'two-factor'}
        onOpenChange={(open) => !open && setConfirm(null)}
        title="Zwei-Faktor-Authentifizierung zurücksetzen?"
        description={`${user.name} muss 2FA bei der nächsten Anmeldung neu einrichten. Bisherige Wiederherstellungscodes werden ungültig.`}
        confirmLabel="2FA zurücksetzen"
        destructive
        onConfirm={resetTwoFactor}
      />
      <ConfirmDialog
        open={confirm === 'impersonate'}
        onOpenChange={(open) => !open && setConfirm(null)}
        title={`Als ${user.name} anmelden?`}
        description="Sie sehen die Anwendung mit den Rechten dieses Benutzers. Alle Aktionen werden im Änderungsprotokoll mit Ihrem Admin-Konto protokolliert. Sie können die Benutzeransicht jederzeit beenden."
        confirmLabel="Benutzeransicht starten"
        onConfirm={impersonate}
      />
    </>
  );
}
